"use client";

import Link from "next/link";
import { memo, useDeferredValue, useEffect, useMemo, useRef, useState, useTransition } from "react";
import { ChevronDown, ChevronUp, CornerDownRight, MessageSquare, Trash2 } from "lucide-react";
import {
  createForumCommentAction,
  markForumThreadSeen,
  reportForumCommentAction,
  removeForumComment,
  searchForumMentionFriends,
  toggleForumThreadFollow,
  voteForumComment,
} from "@/lib/forum-actions";
import { FavoriteTeamMark } from "@/components/site/favorite-team-mark";
import { trackCommentCreate, trackCommentDownvote, trackCommentUpvote } from "@/lib/analytics";
import { UserAvatar } from "@/components/site/user-avatar";
import { UserProfilePopover } from "@/components/site/user-profile-popover";
import { publicUserName } from "@/lib/user-display";
import { formatDate } from "@/lib/utils";
import type { ActionResult } from "@/lib/action-result";
import { CommentComposerTools, CommentRichContent } from "@/components/comments/comment-composer-tools";

export type ForumCommentView = {
  id: string;
  parentId: string | null;
  content: string;
  score: number;
  createdAt: Date | string;
  hiddenAt: Date | string | null;
  removedAt: Date | string | null;
  userId: string;
  user: {
    id: string;
    username: string;
    displayName: string | null;
    image: string | null;
    role: string;
    forumModerator: boolean;
    forumTrusted: boolean;
    favoriteTeam: { name: string; slug: string; logoUrl: string | null; externalTeam?: { crest: string | null } | null } | null;
  };
  currentUserVote: number;
};

type SortMode = "top" | "newest" | "controversial";
type MentionFriend = {
  id: string;
  username: string;
  displayName: string | null;
};

function sortComments(comments: ForumCommentView[], mode: SortMode) {
  const sorted = [...comments];
  if (mode === "newest") {
    sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return sorted;
  }
  if (mode === "controversial") {
    sorted.sort((a, b) => Math.abs(a.score) - Math.abs(b.score));
    return sorted;
  }
  sorted.sort((a, b) => b.score - a.score || new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  return sorted;
}

function countReplySubtree(commentId: string, replyMap: Map<string, ForumCommentView[]>, seen = new Set<string>()): number {
  if (seen.has(commentId)) return 0;
  seen.add(commentId);
  const replies = replyMap.get(commentId) ?? [];
  return replies.reduce((total, reply) => total + 1 + countReplySubtree(reply.id, replyMap, seen), 0);
}

function CommentForm({
  action,
  placeholder,
  submitLabel,
  threadId,
  parentId,
  onDone,
}: {
  action: (formData: FormData) => Promise<ActionResult<Parameters<typeof trackCommentCreate>[0]> | void> | ActionResult<Parameters<typeof trackCommentCreate>[0]> | void;
  placeholder: string;
  submitLabel: string;
  threadId: string;
  parentId?: string;
  onDone?: () => void;
}) {
  const [content, setContent] = useState("");
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionRange, setMentionRange] = useState<{ start: number; end: number } | null>(null);
  const [mentionFriends, setMentionFriends] = useState<MentionFriend[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pending, startSubmitTransition] = useTransition();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mentionRef = useRef<HTMLDivElement>(null);

  function updateMentionState(nextContent: string, cursor: number) {
    const beforeCursor = nextContent.slice(0, cursor);
    const match = beforeCursor.match(/(?:^|\s)@([\p{L}\p{N}_.-]*)$/u);
    if (!match) {
      setMentionQuery(null);
      setMentionRange(null);
      return;
    }
    const token = match[0];
    const atOffset = token.lastIndexOf("@");
    const start = cursor - token.length + atOffset;
    setMentionQuery(match[1] ?? "");
    setMentionRange({ start, end: cursor });
  }

  function insertText(value: string) {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? content.length;
    const end = textarea?.selectionEnd ?? content.length;
    const spacer = start > 0 && !/\s$/.test(content.slice(0, start)) ? " " : "";
    const nextContent = `${content.slice(0, start)}${spacer}${value}${content.slice(end)}`;
    const nextCursor = start + spacer.length + value.length;

    setContent(nextContent);
    window.requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(nextCursor, nextCursor);
    });
  }

  function insertMention(friend: MentionFriend) {
    const range = mentionRange;
    const textarea = textareaRef.current;
    if (!range) return;

    const value = `@${friend.username} `;
    const nextContent = `${content.slice(0, range.start)}${value}${content.slice(range.end)}`;
    const nextCursor = range.start + value.length;
    setContent(nextContent);
    setMentionQuery(null);
    setMentionRange(null);
    setMentionFriends([]);
    window.requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(nextCursor, nextCursor);
    });
  }

  useEffect(() => {
    if (mentionQuery === null) {
      const resetTimer = window.setTimeout(() => setMentionFriends([]), 0);
      return () => window.clearTimeout(resetTimer);
    }

    let active = true;
    const timer = window.setTimeout(() => {
      searchForumMentionFriends(mentionQuery)
        .then((friends) => {
          if (active) setMentionFriends(friends);
        })
        .catch(() => {
          if (active) setMentionFriends([]);
        });
    }, 120);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [mentionQuery]);

  useEffect(() => {
    if (mentionQuery === null) return;

    function handlePointerDown(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (mentionRef.current?.contains(target) || textareaRef.current?.contains(target)) return;
      setMentionQuery(null);
      setMentionRange(null);
    }

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [mentionQuery]);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        const form = event.currentTarget;
        const formData = new FormData(form);
        setSubmitError(null);
        startSubmitTransition(async () => {
          try {
            const result = await action(formData);
            if (result && !result.ok) {
              setSubmitError(result.message);
              return;
            }
            if (result?.data) {
              trackCommentCreate(result.data);
            }
            setContent("");
            form.reset();
            onDone?.();
          } catch (error) {
            console.error("[forum-ui] comment submit failed", error);
            setSubmitError("A hozzászólás most nem sikerült. Próbáld újra később.");
          }
        });
      }}
      className="grid gap-3"
    >
      <input type="hidden" name="threadId" value={threadId} />
      {parentId ? <input type="hidden" name="parentId" value={parentId} /> : null}
      <div className="relative">
        <textarea
          ref={textareaRef}
          name="content"
          value={content}
          onChange={(event) => {
            setContent(event.target.value);
            updateMentionState(event.target.value, event.target.selectionStart);
          }}
          onClick={(event) => updateMentionState(content, event.currentTarget.selectionStart)}
          onKeyUp={(event) => updateMentionState(content, event.currentTarget.selectionStart)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setMentionQuery(null);
              setMentionRange(null);
            }
          }}
          minLength={2}
          required
          placeholder={placeholder}
          className="min-h-24 w-full rounded-[22px] border border-[#D8C99B]/70 bg-[#FCFBF8] px-4 py-3 text-sm text-[#273E47] outline-none transition focus:border-[#A4243B] focus-visible:ring-2 focus-visible:ring-[#A4243B]/20"
        />
        {mentionQuery !== null && mentionFriends.length > 0 ? (
          <div
            ref={mentionRef}
            className="absolute left-3 right-3 top-full z-30 mt-2 overflow-hidden rounded-[16px] border border-[#D8C99B]/70 bg-white shadow-xl sm:right-auto sm:w-72"
          >
            {mentionFriends.map((friend) => (
              <button
                key={friend.id}
                type="button"
                onClick={() => insertMention(friend)}
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition hover:bg-[#F6F1E3] focus-visible:bg-[#F6F1E3] focus-visible:outline-none"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-black text-[#273E47]">
                    {friend.displayName?.trim() || friend.username}
                  </span>
                  <span className="block truncate text-xs font-semibold text-[#273E47]/60">@{friend.username}</span>
                </span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {submitError ? (
        <p className="rounded-[16px] bg-[#A4243B]/10 px-3 py-2 text-sm font-semibold text-[#A4243B]" role="alert">
          {submitError}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-3 overflow-visible">
        <CommentComposerTools onInsert={insertText} />
        <div className="flex justify-end gap-2">
          {onDone ? (
            <button type="button" onClick={onDone} className="rounded-full border border-[#D8C99B]/70 px-3 py-2 text-xs font-black uppercase text-[#273E47]/65 transition hover:border-[#A4243B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#273E47]/20">
              Mégse
            </button>
          ) : null}
          <button disabled={pending} className="rounded-full bg-[#273E47] px-4 py-2 text-xs font-black uppercase text-white transition hover:bg-[#A4243B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/25 disabled:cursor-wait disabled:opacity-60">
            {pending ? "Küldés..." : submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
}

function CommentNode({
  comment,
  replies,
  replyMap,
  currentUserId,
  threadId,
  highlightedCommentIds,
  chatMode,
  isLocked,
  canModerateComments,
  onCommentDeleted,
}: {
  comment: ForumCommentView;
  replies: ForumCommentView[];
  replyMap: Map<string, ForumCommentView[]>;
  currentUserId: string | null;
  threadId: string;
  highlightedCommentIds: Set<string>;
  chatMode: boolean;
  isLocked: boolean;
  canModerateComments: boolean;
  onCommentDeleted: (commentId: string) => void;
}) {
  const [replying, setReplying] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [deletedRootRepliesOpen, setDeletedRootRepliesOpen] = useState(false);
  const hasReplies = replies.length > 0;
  const replyCount = useMemo(() => countReplySubtree(comment.id, replyMap), [comment.id, replyMap]);
  const isHighlighted = highlightedCommentIds.has(comment.id);
  const isDeletedRoot = !comment.parentId && Boolean(comment.removedAt);
  const authorName = publicUserName(comment.user);

  if (isDeletedRoot) {
    return (
      <article className="rounded-[24px] bg-[#EEF0EA] p-4 ring-1 ring-black/5 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-black text-[#273E47]/75">Komment törölve moderáció miatt</p>
          {hasReplies ? (
            <button
              type="button"
              onClick={() => setDeletedRootRepliesOpen((value) => !value)}
              aria-expanded={deletedRootRepliesOpen}
              className="inline-flex items-center gap-1 rounded-full bg-[#FCFBF8] px-3 py-1.5 text-[10px] font-black uppercase text-[#273E47]/70 ring-1 ring-black/5 transition hover:bg-[#E7DFCC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#273E47]/20"
            >
              {deletedRootRepliesOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              {deletedRootRepliesOpen ? "Válaszok elrejtése" : `${replyCount} válasz megjelenítése`}
            </button>
          ) : null}
        </div>

        {hasReplies && deletedRootRepliesOpen ? (
          <div className="mt-4 border-l-2 border-[#D8C99B]/70 pl-3 sm:pl-5">
            <div className="grid gap-3">
              {replies.map((reply) => (
                <MemoizedCommentNode
                  key={reply.id}
                  comment={reply}
                  replies={replyMap.get(reply.id) ?? []}
                  replyMap={replyMap}
                  currentUserId={currentUserId}
                  threadId={threadId}
                  highlightedCommentIds={highlightedCommentIds}
                  chatMode={chatMode}
                  isLocked={isLocked}
                  canModerateComments={canModerateComments}
                  onCommentDeleted={onCommentDeleted}
                />
              ))}
            </div>
          </div>
        ) : null}
      </article>
    );
  }

  return (
    <article
      className={`rounded-[24px] p-4 transition sm:p-5 ${
        isHighlighted ? "bg-[#EEF0EA] ring-1 ring-inset ring-[#D8973C]/30" : "bg-[#F8F6F0] ring-1 ring-black/5"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <UserProfilePopover user={comment.user} currentUserId={currentUserId}>
              <span className="inline-flex min-w-0 items-center gap-2 font-black text-[#273E47]">
                <UserAvatar user={comment.user} size="sm" />
                <span className="truncate">{authorName}</span>
              </span>
            </UserProfilePopover>
            <FavoriteTeamMark team={comment.user.favoriteTeam} />
            {comment.user.role === "ADMIN" || comment.user.role === "EDITOR" ? (
              <span className="rounded-full bg-[#273E47] px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.14em] text-white">
                Verified editor
              </span>
            ) : null}
            {comment.user.forumModerator ? (
              <span className="rounded-full bg-[#A4243B] px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.14em] text-white">
                Moderator
              </span>
            ) : null}
            {comment.user.forumTrusted ? (
              <span className="rounded-full bg-[#D8973C]/15 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.14em] text-[#D8973C]">
                Trusted
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-xs font-bold uppercase tracking-[0.14em] text-[#273E47]/55">
            {formatDate(comment.createdAt)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!chatMode ? (
            <CommentVoteButtons
              commentId={comment.id}
              currentUserId={currentUserId}
              initialVote={comment.currentUserVote}
              initialScore={comment.score}
            />
          ) : null}
          {canModerateComments ? (
            <CommentDeleteButton commentId={comment.id} onDeleted={onCommentDeleted} />
          ) : null}
          <button
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            aria-expanded={!collapsed}
            className="inline-flex items-center gap-1 rounded-full bg-[#EEF0EA] px-3 py-1.5 text-[10px] font-black uppercase text-[#273E47]/70 transition hover:bg-[#E7DFCC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#273E47]/20"
          >
            {collapsed ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
            {collapsed ? "Megnyitás" : "Összecsukás"}
          </button>
        </div>
      </div>

      {!collapsed ? (
        <>
          <CommentRichContent content={comment.content} />
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {!chatMode ? <CommentScoreBadge commentId={comment.id} initialScore={comment.score} /> : null}
            {hasReplies ? (
              <span className="rounded-full bg-[#EEF0EA] px-2.5 py-1 text-[10px] font-black uppercase text-[#D8973C]">
                {replyCount} válasz
              </span>
            ) : null}
            {currentUserId ? (
              <>
                {!isLocked ? (
                  <button
                    type="button"
                    onClick={() => setReplying((value) => !value)}
                    aria-expanded={replying}
                    className="inline-flex items-center gap-1 rounded-full bg-[#EEF0EA] px-3 py-1.5 text-[10px] font-black uppercase text-[#273E47]/70 transition hover:bg-[#E7DFCC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20"
                  >
                    <CornerDownRight size={12} />
                    Válasz
                  </button>
                ) : null}
                {!chatMode ? (
                  <form action={async (formData) => {
                    await reportForumCommentAction(formData);
                  }}>
                    <input type="hidden" name="commentId" value={comment.id} />
                    <input type="hidden" name="reason" value="Comment report" />
                    <button className="rounded-full bg-[#EEF0EA] px-3 py-1.5 text-[10px] font-black uppercase text-[#273E47]/70 transition hover:bg-[#E7DFCC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20">
                      Report
                    </button>
                  </form>
                ) : null}
              </>
            ) : null}
          </div>

          {replying && !isLocked ? (
            <div className="mt-4 rounded-[22px] bg-[#EEF0EA] p-4 ring-1 ring-black/5">
              <CommentForm
                action={createForumCommentAction}
                threadId={threadId}
                parentId={comment.id}
                placeholder={`Válasz ${authorName} hozzászólására`}
                submitLabel="Válasz küldése"
                onDone={() => setReplying(false)}
              />
            </div>
          ) : null}

          {hasReplies ? (
            <div className="mt-5 border-l-2 border-[#D8C99B]/70 pl-3 sm:pl-5">
              <div className="grid gap-3">
                {replies.map((reply) => (
                  <MemoizedCommentNode
                    key={reply.id}
                    comment={reply}
                    replies={replyMap.get(reply.id) ?? []}
                    replyMap={replyMap}
                    currentUserId={currentUserId}
                    threadId={threadId}
                    highlightedCommentIds={highlightedCommentIds}
                    chatMode={chatMode}
                    isLocked={isLocked}
                    canModerateComments={canModerateComments}
                    onCommentDeleted={onCommentDeleted}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </article>
  );
}

const MemoizedCommentNode = memo(CommentNode);

function CommentDeleteButton({
  commentId,
  onDeleted,
}: {
  commentId: string;
  onDeleted: (commentId: string) => void;
}) {
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        disabled={pending}
        title="Komment törlése"
        aria-label="Komment törlése"
        onClick={() => {
          if (!window.confirm("Biztosan törlöd ezt a hozzászólást?")) return;
          setError(null);
          startTransition(async () => {
            const result = await removeForumComment(commentId);
            if (result.ok) {
              onDeleted(commentId);
              return;
            }
            setError(result.message ?? "Nem sikerült törölni a kommentet.");
          });
        }}
        className="inline-flex items-center gap-1 rounded-full bg-[#EEF0EA] px-3 py-1.5 text-[10px] font-black uppercase text-[#273E47]/65 transition hover:bg-[#A4243B]/10 hover:text-[#A4243B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20 disabled:cursor-wait disabled:opacity-60"
      >
        <Trash2 size={12} />
        Törlés
      </button>
      {error ? (
        <span className="max-w-[220px] text-xs font-semibold normal-case text-[#A4243B]" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

const commentScoreEvents = new EventTarget();

function emitCommentScore(commentId: string, score: number) {
  commentScoreEvents.dispatchEvent(new CustomEvent("score", { detail: { commentId, score } }));
}

function CommentScoreBadge({ commentId, initialScore }: { commentId: string; initialScore: number }) {
  const [score, setScore] = useState(initialScore);

  useEffect(() => {
    const timer = window.setTimeout(() => setScore(initialScore), 0);
    return () => window.clearTimeout(timer);
  }, [initialScore]);

  useEffect(() => {
    const handleScore = (event: Event) => {
      const detail = (event as CustomEvent<{ commentId: string; score: number }>).detail;
      if (detail.commentId === commentId) {
        setScore(detail.score);
      }
    };

    commentScoreEvents.addEventListener("score", handleScore);
    return () => commentScoreEvents.removeEventListener("score", handleScore);
  }, [commentId]);

  return (
    <span className="rounded-full bg-[#EEF0EA] px-2.5 py-1 text-[10px] font-black uppercase text-[#273E47]">
      score {score}
    </span>
  );
}

function CommentVoteButtons({
  commentId,
  currentUserId,
  initialVote,
  initialScore,
}: {
  commentId: string;
  currentUserId: string | null;
  initialVote: number;
  initialScore: number;
}) {
  const [vote, setVote] = useState(initialVote);
  const [score, setScore] = useState(initialScore);
  const [pendingVote, setPendingVote] = useState<1 | -1 | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [, startTransition] = useTransition();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setVote(initialVote);
      setScore(initialScore);
      emitCommentScore(commentId, initialScore);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [commentId, initialScore, initialVote]);

  function handleVote(value: 1 | -1) {
    if (!currentUserId || pendingVote) return;

    const previousVote = vote;
    const previousScore = score;
    const nextVote = previousVote === value ? 0 : value;
    const nextScore = previousScore + nextVote - previousVote;

    setVote(nextVote);
    setScore(nextScore);
    emitCommentScore(commentId, nextScore);
    setPendingVote(value);

    startTransition(async () => {
      try {
        const result = await voteForumComment(commentId, value);
        if (!result.ok) {
          setVote(previousVote);
          setScore(previousScore);
          emitCommentScore(commentId, previousScore);
          setMessage(result.message);
          return;
        }
        if (!result.data) throw new Error("missing_vote_result");
        const data = result.data;
        setVote(data.currentVote);
        setScore(data.score);
        emitCommentScore(commentId, data.score);
        if (value === 1 && data.currentVote === 1) {
          trackCommentUpvote({ commentId, threadId: data.threadId });
        } else if (value === -1 && data.currentVote === -1) {
          trackCommentDownvote({ commentId, threadId: data.threadId });
        }
      } catch (error) {
        setVote(previousVote);
        setScore(previousScore);
        emitCommentScore(commentId, previousScore);
        console.error("[forum-ui] comment vote failed", error);
        setMessage("A szavazás most nem sikerült.");
      } finally {
        setPendingVote(null);
      }
    });
  }

  return (
    <>
      <button
        type="button"
        disabled={!currentUserId || pendingVote === 1}
        aria-label="Upvote comment"
        onClick={() => handleVote(1)}
        className={`rounded-full px-3 py-1.5 text-[10px] font-black uppercase transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20 ${
          vote === 1 ? "bg-[#A4243B]/10 text-[#A4243B] ring-1 ring-inset ring-[#A4243B]/20" : "bg-[#EEF0EA] text-[#273E47]/75 hover:bg-[#E7DFCC]"
        } disabled:opacity-40`}
      >
        {pendingVote === 1 ? "..." : "+1"}
      </button>
      <button
        type="button"
        disabled={!currentUserId || pendingVote === -1}
        aria-label="Downvote comment"
        onClick={() => handleVote(-1)}
        className={`rounded-full px-3 py-1.5 text-[10px] font-black uppercase transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#273E47]/20 ${
          vote === -1 ? "bg-[#273E47]/10 text-[#273E47] ring-1 ring-inset ring-[#273E47]/20" : "bg-[#EEF0EA] text-[#273E47]/75 hover:bg-[#E7DFCC]"
        } disabled:opacity-40`}
      >
        {pendingVote === -1 ? "..." : "-1"}
      </button>
      {message ? <span className="text-[10px] font-semibold text-[#A4243B]">{message}</span> : null}
    </>
  );
}

function ThreadFollowToggle({
  threadId,
  initialFollowing,
}: {
  threadId: string;
  initialFollowing: boolean;
}) {
  const [following, setFollowing] = useState(initialFollowing);
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [, startTransition] = useTransition();

  useEffect(() => {
    const timer = window.setTimeout(() => setFollowing(initialFollowing), 0);
    return () => window.clearTimeout(timer);
  }, [initialFollowing]);

  function handleChange() {
    if (pending) return;

    const previousFollowing = following;
    setFollowing(!previousFollowing);
    setPending(true);

    startTransition(async () => {
      try {
        const result = await toggleForumThreadFollow(threadId);
        if (!result.ok) {
          setFollowing(previousFollowing);
          setMessage(result.message);
          return;
        }
        if (!result.data) throw new Error("missing_follow_result");
        setFollowing(result.data.following);
      } catch (error) {
        setFollowing(previousFollowing);
        console.error("[forum-ui] thread follow failed", error);
        setMessage("A követés most nem sikerült.");
      } finally {
        setPending(false);
      }
    });
  }

  return (
    <label className="mb-4 flex items-center justify-between gap-3 rounded-[20px] bg-[#EEF0EA] px-4 py-3 text-sm font-bold text-[#273E47] ring-1 ring-black/5">
      <span>Kövesd ezt a threadet értesítésekért</span>
      {message ? <span className="text-xs text-[#A4243B]">{message}</span> : null}
      <input
        type="checkbox"
        checked={following}
        disabled={pending}
        onChange={handleChange}
        aria-label="Follow thread for notifications"
        className="h-5 w-5 accent-[#A4243B] disabled:opacity-50"
      />
    </label>
  );
}

const MemoizedCommentList = memo(function CommentList({
  roots,
  replyMap,
  currentUserId,
  threadId,
  highlightedCommentSet,
  chatMode,
  isLocked,
  canModerateComments,
  onCommentDeleted,
}: {
  roots: ForumCommentView[];
  replyMap: Map<string, ForumCommentView[]>;
  currentUserId: string | null;
  threadId: string;
  highlightedCommentSet: Set<string>;
  chatMode: boolean;
  isLocked: boolean;
  canModerateComments: boolean;
  onCommentDeleted: (commentId: string) => void;
}) {
  return (
    <div className="mt-5 grid gap-4">
      {roots.length ? (
        roots.map((comment) => (
          <MemoizedCommentNode
            key={comment.id}
            comment={comment}
            replies={replyMap.get(comment.id) ?? []}
            replyMap={replyMap}
            currentUserId={currentUserId}
            threadId={threadId}
            highlightedCommentIds={highlightedCommentSet}
            chatMode={chatMode}
            isLocked={isLocked}
            canModerateComments={canModerateComments}
            onCommentDeleted={onCommentDeleted}
          />
        ))
      ) : (
        <div className="rounded-[24px] bg-[#F8F6F0] p-6 text-center ring-1 ring-black/5">
          <MessageSquare size={20} className="mx-auto text-[#A4243B]" />
          <p className="mt-3 text-sm font-semibold text-[#273E47]/75">
            Még nincs hozzászólás. Itt indulhat el a meccsnapos vita vagy az első erős vélemény.
          </p>
        </div>
      )}
    </div>
  );
});

export function ForumCommentSection({
  threadId,
  currentUserId,
  isFollowing,
  comments,
  isLocked = false,
  lockedMessage = "Ez a thread le van zárva, új hozzászólás nem küldhető.",
  highlightedCommentIds = [],
  newCommentCount = 0,
  isPolling = false,
  chatMode = false,
  canModerateComments = false,
}: {
  threadId: string;
  currentUserId: string | null;
  isFollowing: boolean;
  comments: ForumCommentView[];
  isLocked?: boolean;
  lockedMessage?: string;
  highlightedCommentIds?: string[];
  newCommentCount?: number;
  isPolling?: boolean;
  chatMode?: boolean;
  canModerateComments?: boolean;
}) {
  const [sort, setSort] = useState<SortMode>(chatMode ? "newest" : "top");
  const [, startTransition] = useTransition();
  const [deletedCommentIds, setDeletedCommentIds] = useState<Set<string>>(() => new Set());
  const [deletedRootCommentIds, setDeletedRootCommentIds] = useState<Set<string>>(() => new Set());
  const visibleComments = useMemo(
    () =>
      comments
        .filter((comment) => !deletedCommentIds.has(comment.id))
        .map((comment) =>
          deletedRootCommentIds.has(comment.id)
            ? { ...comment, removedAt: comment.removedAt ?? new Date().toISOString() }
            : comment,
        ),
    [comments, deletedCommentIds, deletedRootCommentIds],
  );
  const deferredComments = useDeferredValue(visibleComments);
  const visibleCommentCount = useMemo(
    () => visibleComments.filter((comment) => !comment.removedAt).length,
    [visibleComments],
  );
  const highlightedCommentSet = useMemo(() => new Set(highlightedCommentIds), [highlightedCommentIds]);
  const handleCommentDeleted = (commentId: string) => {
    const deletedComment = comments.find((comment) => comment.id === commentId);
    if (!deletedComment?.parentId) {
      setDeletedRootCommentIds((existing) => {
        const next = new Set(existing);
        next.add(commentId);
        return next;
      });
      return;
    }

    setDeletedCommentIds((existing) => {
      const next = new Set(existing);
      next.add(commentId);
      return next;
    });
  };

  useEffect(() => {
    if (currentUserId && isFollowing) {
      startTransition(() => {
        void markForumThreadSeen(threadId);
      });
    }
  }, [currentUserId, isFollowing, threadId]);

  const roots = useMemo(
    () => sortComments(deferredComments.filter((comment) => !comment.parentId), sort),
    [deferredComments, sort],
  );
  const replyMap = useMemo(() => {
    const map = new Map<string, ForumCommentView[]>();
    deferredComments
      .filter((comment) => comment.parentId)
      .forEach((comment) => {
        const key = comment.parentId ?? "";
        const existing = map.get(key) ?? [];
        map.set(key, sortComments([...existing, comment], sort));
      });
    return map;
  }, [deferredComments, sort]);

  return (
    <section className="rounded-[30px] bg-[#F8F6F0] p-4 ring-1 ring-black/5 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-[clamp(1.35rem,2vw,1.8rem)] font-black uppercase text-[#273E47]">Beszélgetés</h2>
          <p className="text-sm font-semibold text-[#273E47]/65">{visibleCommentCount} hozzászólás</p>
        </div>
        {!chatMode ? (
          <div className="flex flex-wrap items-center gap-2">
            {(["top", "newest", "controversial"] as SortMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setSort(mode)}
                className={`rounded-full px-3 py-2 text-[10px] font-black uppercase tracking-[0.14em] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#273E47]/20 ${
                  sort === mode ? "bg-[#273E47] text-white" : "bg-[#EEF0EA] text-[#273E47]/70 hover:bg-[#E7DFCC]"
                }`}
              >
                {mode}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {newCommentCount > 0 || isPolling ? (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-[20px] bg-[#EEF0EA] px-4 py-3 text-sm font-semibold text-[#273E47]/75 ring-1 ring-black/5">
          {newCommentCount > 0 ? <span className="text-[#A4243B]">{newCommentCount} új komment érkezett a legutóbbi frissítés óta.</span> : null}
          {isPolling ? <span>Élő frissítés folyamatban...</span> : null}
        </div>
      ) : null}

      <div className="mt-4 rounded-[24px] bg-[#FCFBF8] p-4 ring-1 ring-black/5">
        {currentUserId ? (
          <>
            {!chatMode ? <ThreadFollowToggle threadId={threadId} initialFollowing={isFollowing} /> : null}
            {isLocked ? (
              <div className="rounded-[20px] bg-[#F8F6F0] px-4 py-4 text-sm font-semibold text-[#273E47]/75 ring-1 ring-black/5">
                {lockedMessage}
              </div>
            ) : (
              <CommentForm
                action={createForumCommentAction}
                threadId={threadId}
                placeholder="Gyors komment mód: reakció, vélemény, meccs közben instant benyomás."
                submitLabel="Komment küldése"
              />
            )}
          </>
        ) : (
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            {isLocked ? (
              <p className="text-sm font-bold text-[#273E47]/75">
                {lockedMessage}
              </p>
            ) : (
              <>
                <p className="text-sm font-bold text-[#273E47]/75">
                  Hozzászóláshoz <Link href="/login" className="text-[#A4243B] underline underline-offset-4">jelentkezz be</Link>.
                </p>
                <Link href="/login" className="inline-flex rounded-full bg-[#A4243B] px-4 py-2 text-xs font-black uppercase tracking-[0.14em] text-white transition hover:bg-[#273E47] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/25">
                  Kapcsolódj be
                </Link>
              </>
            )}
          </div>
        )}
      </div>

      <MemoizedCommentList
        roots={roots}
        replyMap={replyMap}
        currentUserId={currentUserId}
        threadId={threadId}
        highlightedCommentSet={highlightedCommentSet}
        chatMode={chatMode}
        isLocked={isLocked}
        canModerateComments={canModerateComments}
        onCommentDeleted={handleCommentDeleted}
      />
    </section>
  );
}
